import React, { useEffect, useState } from "react";
import Footer from "../layout/Footer";
import PublicHeader from "../layout/PublicHeader";

/**
 * ValidationPanel
 * Same props / behaviour as before. Only the UI changed to match the
 * TalentTalks "Access Denied" reference pages (used / expired / not-active).
 *
 * Existing props (unchanged): variant, title, description, scheduled_date,
 * scheduled_time, interviewTimezone, actions
 *
 * Optional new props (UI text only, safe defaults):
 *  - note          : small grey line under the description (e.g. the
 *                    "valid for 2 minutes" text on the expired page).
 *                    When provided, the "document" illustration is used
 *                    (matches the expired reference).
 *  - scheduleLabel : label above the schedule box. Default "Scheduled on"
 *                    (use "Scheduled at" for the not-yet-active page).
 */
export default function ValidationPanel({
  variant = "info", // kept for compatibility
  title,
  description,
  scheduled_date,
  scheduled_time,
  interviewTimezone,
  actions = null,
  note = null,
  scheduleLabel = "Scheduled on",
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const t = setTimeout(() => setMounted(true), 60);
      return () => clearTimeout(t);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const useDocIllustration = Boolean(note);

  return (
    <div className="vp-root">
      <style>{css}</style>

      {/* Background decoration */}
      <div className="vp-bg" aria-hidden="true">
        <span className="blob b1" />
        <span className="blob b2" />
        <span className="dots d1" />
        <span className="dots d2" />
      </div>

      <PublicHeader />

      <main className="vp-main">
        <div className="vp-wrap">
          <section
            className={`vp-card ${mounted ? "in" : ""}`}
            aria-labelledby="adTitle"
          >
            <div className="vp-content">
              <div className="vp-head" data-anim style={{ "--d": ".1s" }}>
                <span className="vp-xbadge" aria-hidden="true">
                  <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
                    <path
                      d="M10 10l30 30"
                      stroke="#E8405A"
                      strokeWidth="7"
                      strokeLinecap="round"
                    />
                    <path
                      d="M40 10L10 40"
                      stroke="#E8405A"
                      strokeWidth="7"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <div>
                  <h1 id="adTitle">{title}</h1>
                  <p className={note ? "sub-exp" : "sub-strong"}>
                    {description ||
                      "There was an issue with this interview link."}
                  </p>
                  {note && <p className="sub-note">{note}</p>}
                </div>
              </div>

              <p className="vp-reasons-intro" data-anim style={{ "--d": ".3s" }}>
                Here are a few possible reasons:
              </p>
              <ul className="vp-reasons">
                <li>Link was generated for a different candidate or session.</li>
                <li>
                  Interview window has not started yet or has already ended.
                </li>
                <li>
                  There was an internal validation error or the link has been
                  revoked.
                </li>
              </ul>

              {scheduled_date && scheduled_time && (
                <div className="vp-sched" data-anim style={{ "--d": ".8s" }}>
                  <small>
                    <span className="pulse" aria-hidden="true" />
                    {scheduleLabel}
                  </small>
                  <div className="when">
                    <strong>
                      {scheduled_date} · {scheduled_time}
                    </strong>
                    {interviewTimezone && (
                      <span className="tz">({interviewTimezone})</span>
                    )}
                  </div>
                </div>
              )}

              <div className="vp-actions" data-anim style={{ "--d": ".9s" }}>
                {actions ? (
                  actions
                ) : (
                  <>
                    <a href="/" className="vp-btn vp-btn-home">
                      Go to Home
                      <svg
                        className="arr"
                        width="22"
                        height="22"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 8h10M9 4l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                    <a href="/support" className="vp-btn-ghost">
                      Contact support
                    </a>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="vp-btn-ghost"
                    >
                      Retry
                    </button>
                  </>
                )}
              </div>

              <p className="vp-help" data-anim style={{ "--d": "1s" }}>
                Need help? Reach out to our support team and include your
                interview ID for faster assistance.
              </p>
            </div>

            {/* Illustration (decorative) */}
            <div className="vp-art" data-anim style={{ "--d": ".25s" }} aria-hidden="true">
              {useDocIllustration ? <DocIllustration /> : <WindowIllustration />}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function WindowIllustration() {
  return (
    <svg viewBox="0 0 320 300" fill="none">
      <defs>
        <linearGradient id="vpWin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#DCCDFB" />
          <stop offset="1" stopColor="#E9E1FC" />
        </linearGradient>
        <linearGradient id="vpCardG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F4EFFF" />
          <stop offset="1" stopColor="#DCCBFA" />
        </linearGradient>
        <linearGradient id="vpPerson" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#A57BF7" />
          <stop offset="1" stopColor="#7C4DE8" />
        </linearGradient>
        <linearGradient id="vpRedG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF5C74" />
          <stop offset="1" stopColor="#E8304C" />
        </linearGradient>
        <filter id="vpSh" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#4B1498" floodOpacity=".18" />
        </filter>
      </defs>
      <ellipse cx="155" cy="232" rx="120" ry="34" fill="#E7DEFB" />
      <g className="float">
        <rect x="48" y="40" width="226" height="190" rx="16" fill="url(#vpWin)" opacity=".85" />
        <circle className="dot1" cx="70" cy="60" r="5" fill="#A88DEB" />
        <circle className="dot2" cx="88" cy="60" r="5" fill="#A88DEB" />
        <circle className="dot3" cx="106" cy="60" r="5" fill="#A88DEB" />
        <rect x="100" y="84" width="132" height="138" rx="16" fill="url(#vpCardG)" stroke="#fff" strokeWidth="4" filter="url(#vpSh)" />
        <circle cx="166" cy="130" r="22" fill="url(#vpPerson)" />
        <path d="M126 196c4-24 20-38 40-38s36 14 40 38" stroke="url(#vpPerson)" strokeWidth="10" strokeLinecap="round" fill="none" />
      </g>
      <circle className="badge-ring" cx="228" cy="196" r="38" stroke="#FF5C74" strokeWidth="3" fill="none" />
      <g className="badge">
        <circle cx="228" cy="196" r="38" fill="#fff" filter="url(#vpSh)" />
        <circle cx="228" cy="196" r="31" fill="url(#vpRedG)" />
        <path d="M216 184l24 24M240 184l-24 24" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
      </g>
      <path className="sp" d="M20 136l20 10" stroke="#C9B3F5" strokeWidth="7" strokeLinecap="round" />
      <path className="sp" d="M14 166l10-3" stroke="#C9B3F5" strokeWidth="7" strokeLinecap="round" />
      <path className="sp" d="M286 140l10-8" stroke="#C9B3F5" strokeWidth="7" strokeLinecap="round" />
      <path className="sp" d="M296 176l8 2" stroke="#F7A6B4" strokeWidth="7" strokeLinecap="round" />
    </svg>
  );
}

function DocIllustration() {
  return (
    <svg viewBox="0 0 320 300" fill="none">
      <defs>
        <linearGradient id="vpPersonE" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#B08AF8" />
          <stop offset="1" stopColor="#7C4DE8" />
        </linearGradient>
        <filter id="vpShE" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#4B1498" floodOpacity=".14" />
        </filter>
      </defs>
      <circle cx="118" cy="110" r="66" fill="#EDE6FC" />
      <circle cx="232" cy="218" r="40" fill="#EDE6FC" />
      <circle className="dotf d1" cx="252" cy="52" r="9" fill="#E3D9FA" />
      <circle className="dotf d2" cx="270" cy="124" r="7" fill="#E3D9FA" />
      <circle className="dotf d3" cx="48" cy="176" r="7" fill="#E3D9FA" />
      <g className="float">
        <rect x="96" y="70" width="128" height="160" rx="14" fill="#fff" filter="url(#vpShE)" />
        <rect x="122" y="90" width="80" height="76" rx="10" fill="#E9E0FC" />
        <circle cx="162" cy="116" r="15" fill="url(#vpPersonE)" />
        <path d="M136 158c4-16 14-24 26-24s22 8 26 24" stroke="url(#vpPersonE)" strokeWidth="7" strokeLinecap="round" fill="none" />
        <rect x="118" y="182" width="62" height="8" rx="4" fill="#E3DAFA" />
        <rect x="118" y="198" width="42" height="8" rx="4" fill="#E3DAFA" />
      </g>
      <g className="badge doc">
        <circle cx="222" cy="170" r="24" fill="#FBDDE3" />
        <path d="M213 161l18 18M231 161l-18 18" stroke="#E8405A" strokeWidth="5.5" strokeLinecap="round" />
      </g>
      <path className="sp" d="M254 132l8-10" stroke="#F49AAE" strokeWidth="5" strokeLinecap="round" />
      <path className="sp" d="M262 148h10" stroke="#F49AAE" strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.vp-root{
  --primary:#4B1498; --violet:#A66FFF; --ink:#191F2D; --text:#4A5163; --muted:#6B7185;
  --ease:cubic-bezier(.22,.61,.36,1); --spring:cubic-bezier(.34,1.56,.64,1);
  font-family:"Inter",system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;
  color:var(--text); font-size:15px; line-height:1.5;
  min-height:100vh; display:flex; flex-direction:column; overflow-x:hidden;
  background:linear-gradient(180deg,#F6F3FD 0%,#FAF8FF 100%);
  -webkit-font-smoothing:antialiased; position:relative;
}
.vp-root *,.vp-root *::before,.vp-root *::after{box-sizing:border-box}
.vp-root ul{list-style:none;margin:0;padding:0}
.vp-root a{color:inherit;text-decoration:none}
.vp-root :focus-visible{outline:3px solid rgba(166,111,255,.55);outline-offset:2px;border-radius:8px}
.vp-wrap{width:100%;max-width:1440px;margin:0 auto;padding:0 clamp(16px,3vw,44px)}

/* Background */
.vp-bg{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
.vp-bg .blob{position:absolute;border-radius:50%}
.vp-bg .b1{width:620px;height:620px;left:-320px;bottom:-160px;background:radial-gradient(closest-side,#ECE3FF 55%,rgba(236,227,255,0));animation:vpDrift 24s ease-in-out infinite alternate}
.vp-bg .b2{width:680px;height:680px;right:-360px;top:18%;background:radial-gradient(closest-side,#EBE1FF 55%,rgba(235,225,255,0));animation:vpDrift 28s ease-in-out infinite alternate-reverse}
.vp-bg .dots{position:absolute;width:76px;height:76px;background-image:radial-gradient(#D3C6F0 2.2px,transparent 2.6px);background-size:28px 28px}
.vp-bg .d1{left:4%;top:150px}
.vp-bg .d2{right:4%;bottom:80px}
@keyframes vpDrift{to{transform:translate(40px,-30px)}}

.vp-root > header,.vp-root > footer{position:relative;z-index:5}

/* Main card */
.vp-main{position:relative;z-index:1;flex:1;display:flex;align-items:center;padding:clamp(36px,7vh,70px) 0}
.vp-card{max-width:1090px;margin:0 auto;display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);gap:clamp(28px,3.5vw,40px);align-items:center;
  background:rgba(255,255,255,.94);border:1px solid #E6DEF8;border-radius:24px;padding:clamp(28px,3.4vw,40px) clamp(24px,3.4vw,44px);box-shadow:0 30px 70px -36px rgba(75,20,152,.32)}
.vp-head{display:flex;align-items:flex-start;gap:26px}
.vp-xbadge{width:100px;height:100px;flex:none;border-radius:50%;background:#FDE8EC;display:grid;place-items:center;position:relative}
.vp-xbadge::after{content:"";position:absolute;inset:-6px;border-radius:50%;border:2px solid rgba(232,63,90,.28);animation:vpPing 2.6s var(--ease) 1.2s infinite}
.vp-xbadge path{stroke-dasharray:60;stroke-dashoffset:60}
.in .vp-xbadge path{animation:vpDraw .45s var(--ease) forwards}
.in .vp-xbadge path:nth-child(2){animation-delay:.25s}
.in .vp-xbadge svg{animation:vpShake .5s var(--ease) .8s}
@keyframes vpDraw{to{stroke-dashoffset:0}}
@keyframes vpShake{20%{transform:translateX(-5px)}40%{transform:translateX(5px)}60%{transform:translateX(-3px)}80%{transform:translateX(2px)}}
@keyframes vpPing{0%{transform:scale(.92);opacity:1}100%{transform:scale(1.3);opacity:0}}
.vp-head h1{margin:6px 0 0;color:var(--ink);font-size:clamp(30px,2.9vw,40px);font-weight:800;letter-spacing:-.02em;line-height:1.15}
.vp-head p{margin:6px 0 0;font-size:clamp(17px,1.5vw,20px);color:var(--muted)}
.vp-head .sub-strong{font-weight:600;color:#6B6F92}
.vp-head .sub-exp{font-size:clamp(17px,1.5vw,19px);font-weight:500;color:var(--ink)}
.vp-head .sub-note{font-size:15px;color:var(--muted)}
.vp-reasons-intro{margin:30px 0 0;font-size:17px;color:var(--text)}
.vp-reasons{display:grid;gap:8px;margin-top:14px}
.vp-reasons li{display:flex;gap:14px;align-items:baseline;font-size:clamp(15px,1.3vw,17.5px);line-height:1.5;color:var(--ink)}
.vp-reasons li::before{content:"";width:8px;height:8px;flex:none;border-radius:50%;background:var(--primary);transform:translateY(-2px)}
.in .vp-reasons li{animation:vpSlideIn .5s var(--ease) both}
.in .vp-reasons li:nth-child(1){animation-delay:.5s}
.in .vp-reasons li:nth-child(2){animation-delay:.6s}
.in .vp-reasons li:nth-child(3){animation-delay:.7s}
@keyframes vpSlideIn{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:none}}

/* Schedule box */
.vp-sched{position:relative;overflow:hidden;margin-top:26px;padding:16px 20px;border-radius:12px;background:#fff;border:1px solid #DED7EE}
.vp-sched::after{content:"";position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.7) 50%,transparent 70%);transform:translateX(-120%)}
.in .vp-sched::after{animation:vpSweep 1.2s var(--ease) 1.1s}
@keyframes vpSweep{to{transform:translateX(120%)}}
.vp-sched small{display:block;font-size:14.5px;color:var(--muted)}
.vp-sched .when{display:flex;align-items:baseline;flex-wrap:wrap;gap:6px 14px;margin-top:4px}
.vp-sched strong{font-size:clamp(18px,1.6vw,20px);font-weight:700;color:var(--ink);letter-spacing:-.01em}
.vp-sched .tz{font-size:17px;color:var(--text)}
.vp-sched .pulse{display:inline-block;width:8px;height:8px;margin-right:8px;border-radius:50%;background:var(--violet);vertical-align:middle;box-shadow:0 0 0 0 rgba(166,111,255,.6);animation:vpPulse 2s ease-out infinite}
@keyframes vpPulse{0%{box-shadow:0 0 0 0 rgba(166,111,255,.6)}70%{box-shadow:0 0 0 8px rgba(166,111,255,0)}100%{box-shadow:0 0 0 0 rgba(166,111,255,0)}}

/* Buttons */
.vp-actions{display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin-top:28px}
.vp-btn{position:relative;overflow:hidden;isolation:isolate;display:inline-flex;align-items:center;justify-content:center;gap:14px;border:0;border-radius:10px;font-weight:600;cursor:pointer;
  color:#fff;background:linear-gradient(90deg,#4B1498 0%,#5B1FC0 55%,#7A3BEF 100%);background-size:160% 100%;box-shadow:0 16px 30px -14px rgba(75,20,152,.65);
  transition:transform .25s var(--ease),background-position .5s var(--ease)}
.vp-btn::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(110deg,transparent 25%,rgba(255,255,255,.25) 45%,transparent 65%);transform:translateX(-120%);animation:vpShine 4.5s var(--ease) 2s infinite}
@keyframes vpShine{0%{transform:translateX(-120%)}35%,100%{transform:translateX(120%)}}
.vp-btn:hover{background-position:100% 0;transform:translateY(-2px)}
.vp-btn .arr{transition:transform .3s var(--spring)}
.vp-btn:hover .arr{transform:translateX(4px)}
.vp-btn-home{height:54px;padding:0 30px;font-size:19px}
.vp-btn-ghost{display:inline-flex;align-items:center;height:54px;padding:0 22px;border-radius:10px;border:1px solid #DCD3F2;background:#fff;color:var(--primary);font:inherit;font-size:16px;font-weight:600;cursor:pointer;transition:background-color .2s,transform .25s var(--ease)}
.vp-btn-ghost:hover{background:#F1EDFB;transform:translateY(-2px)}
/* Force purple styling on buttons passed in through the "actions" prop
   (e.g. ones that still carry teal Tailwind classes). Functionality untouched. */
.vp-actions > a:first-child,
.vp-actions > button:first-child{
  display:inline-flex!important;align-items:center;justify-content:center;gap:14px;
  height:54px;padding:0 30px!important;border:0!important;border-radius:10px!important;
  font-size:19px!important;font-weight:600!important;color:#fff!important;cursor:pointer;
  background:linear-gradient(90deg,#4B1498 0%,#5B1FC0 55%,#7A3BEF 100%)!important;
  background-size:160% 100%!important;box-shadow:0 16px 30px -14px rgba(75,20,152,.65)!important;
  transition:transform .25s var(--ease),background-position .5s var(--ease);
}
.vp-actions > a:first-child:hover,
.vp-actions > button:first-child:hover{background-position:100% 0!important;transform:translateY(-2px)}
.vp-actions > a:not(:first-child),
.vp-actions > button:not(:first-child){
  display:inline-flex!important;align-items:center;height:54px;padding:0 22px!important;
  border-radius:10px!important;border:1px solid #DCD3F2!important;
  background:#fff!important;color:#4B1498!important;font-size:16px!important;font-weight:600!important;cursor:pointer;
}
.vp-actions > a:not(:first-child):hover,
.vp-actions > button:not(:first-child):hover{background:#F1EDFB!important}
.vp-help{margin:28px 0 0;max-width:540px;font-size:14px;line-height:1.5;color:var(--muted)}

/* Illustration */
.vp-art{border-radius:20px;background:linear-gradient(160deg,#F5F1FE,#EFE9FC);border:1px solid #E8E0FA;aspect-ratio:1.0;max-height:410px;display:grid;place-items:center;overflow:hidden}
.vp-art svg{width:86%;height:auto;overflow:visible}
.vp-art .float{animation:vpFloat 6s ease-in-out infinite}
.vp-art .badge{transform-origin:228px 196px;animation:vpBadgePop .6s var(--spring) .9s both}
.vp-art .badge.doc{transform-origin:222px 170px}
.vp-art .badge-ring{transform-origin:228px 196px;animation:vpRing 2.4s var(--ease) 1.6s infinite}
.vp-art .sp{animation:vpBlink 2.4s ease-in-out infinite}
.vp-art .sp:nth-of-type(2){animation-delay:.4s}
.vp-art .sp:nth-of-type(3){animation-delay:.8s}
.vp-art .sp:nth-of-type(4){animation-delay:1.2s}
.vp-art .dot1{animation:vpBlink 1.8s ease-in-out infinite}
.vp-art .dot2{animation:vpBlink 1.8s ease-in-out .3s infinite}
.vp-art .dot3{animation:vpBlink 1.8s ease-in-out .6s infinite}
.vp-art .dotf{animation:vpBob 4s ease-in-out infinite}
.vp-art .dotf.d2{animation-delay:-1.3s}
.vp-art .dotf.d3{animation-delay:-2.6s}
@keyframes vpFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
@keyframes vpBadgePop{from{transform:scale(0)}to{transform:scale(1)}}
@keyframes vpRing{0%{transform:scale(1);opacity:.7}100%{transform:scale(1.45);opacity:0}}
@keyframes vpBlink{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes vpBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}

/* Entrance */
.vp-root [data-anim]{opacity:0;transform:translateY(18px);transition:opacity .7s var(--ease),transform .7s var(--ease);transition-delay:var(--d,0s)}
.vp-root .in [data-anim],.vp-root [data-anim].in{opacity:1;transform:none}

/* Responsive */
@media (max-width:980px){
  .vp-card{grid-template-columns:1fr}
  .vp-art{max-height:none;aspect-ratio:1.5;order:-1}
  .vp-art svg{width:min(70%,380px)}
}
@media (max-width:560px){
  .vp-bg .dots{display:none}
  .vp-card{border-radius:18px;padding:20px 18px 26px}
  .vp-art{aspect-ratio:1.25}
  .vp-art svg{width:82%}
  .vp-head{gap:16px}
  .vp-xbadge{width:68px;height:68px}
  .vp-xbadge svg{width:34px;height:34px}
  .vp-reasons-intro{margin-top:26px}
  .vp-actions{flex-direction:column;align-items:stretch}
  .vp-btn-home,.vp-btn-ghost{width:100%;justify-content:center}
  .vp-help{margin-top:24px}
}
@media (prefers-reduced-motion:reduce){
  .vp-root *,.vp-root *::before,.vp-root *::after{animation:none!important;transition:none!important}
  .vp-root [data-anim]{opacity:1;transform:none}
  .vp-xbadge path{stroke-dashoffset:0}
}
`;